import { useState } from 'react';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import { Loader, LoaderModeType } from 'components/Loader';
import { TotemPopup } from 'components/TotemPopup';
import { TotemPrimaryButton } from 'components/TotemPrimaryButton';
import { TotemSelect } from 'components/TotemSelect';

import { TotemDatePicker } from 'components/TotemDatePicker';
import { DeliveryDestinationType, ProductFlow, useCreateBundlesMutation, TotadminPermission } from 'data/__generated__';
import { formatDateAsAnniversary } from 'helpers/dateTimes';
import { UltraSelect } from 'components/UltraSelect';
import { warehouseColumnSource } from 'data/entity-sources/sources';

const FLOW_OPTIONS = [
    { label: 'Sec', value: ProductFlow.Dry },
    {
        label: 'Frais',
        value: ProductFlow.Fresh,
    },
    {
        label: 'Kumo',
        value: ProductFlow.Kumo,
    },
    {
        label: 'Fruits',
        value: ProductFlow.Fruit,
    },
    {
        label: 'Boulangerie',
        value: ProductFlow.Bakery,
    },
    {
        label: 'Traiteur',
        value: ProductFlow.Caterer,
    },
];
const DELIVERY_TYPES = [
    {
        label: 'Store',
        value: DeliveryDestinationType.Store,
    },
    {
        label: 'Freefood',
        value: DeliveryDestinationType.Freefood,
    },
];

export function CreateBundlesPopup({ isOpen, setIsOpen }: { isOpen: boolean; setIsOpen: (arg: boolean) => void }) {
    const [selectedWarehouseColumnId, setSelectedWarehouseColumnId] = useState<string | null>(null);
    const [selectedFlow, setSelectedFlow] = useState<ProductFlow | null>(null);
    const [selectedType, setSelectedType] = useState<DeliveryDestinationType | null>(null);
    const [deliveryDate, setDeliveryDate] = useState<Date>(new Date());

    const [createBundles, { loading }] = useCreateBundlesMutation();

    async function handleSubmit() {
        if (deliveryDate && selectedFlow && selectedType && selectedWarehouseColumnId) {
            const { data } = await createBundles({
                variables: {
                    flow: selectedFlow,
                    deliveryType: selectedType,
                    deliveryDate: formatDateAsAnniversary({ dateTime: deliveryDate }),
                    warehouseColumnId: selectedWarehouseColumnId,
                },
            });

            if (!data) {
                throw new Error('Une erreur est survenue lors de la création de la livraison');
            }

            const { createBundles: result } = data;

            if (result.success) {
                toast.success('Bundles créés !');
                setIsOpen(false);
            } else {
                result?.errors?.forEach((error, index) => {
                    toast.error(<span key={index}>Erreur : {error}</span>, { autoClose: false });
                });

                if (result?.errors?.length && result?.errors?.length > 1) {
                    toast.info('Cliquez pour fermer toutes les notifications', {
                        autoClose: false,
                        onClick: () => toast.dismiss(),
                    });
                }
            }
        }
    }

    return (
        <TotemPopup title="Créer des bundles" isOpen={isOpen} setIsOpen={setIsOpen} contentOverflow="visible">
            <SelectorContainer>
                <UltraSelect
                    source={warehouseColumnSource}
                    label="Entrepôt"
                    placeholder="Sélectionner un entrepôt"
                    value={selectedWarehouseColumnId}
                    onChange={setSelectedWarehouseColumnId}
                    dataTest="create-bundles-warehouse-select"
                />
            </SelectorContainer>
            <SelectorContainer>
                <TotemSelect
                    label="Flux"
                    placeholder="Sélectionner un flux"
                    value={selectedFlow}
                    options={FLOW_OPTIONS}
                    onChange={setSelectedFlow}
                    dataTest="create-bundles-flow-select"
                />
            </SelectorContainer>
            {selectedFlow ? (
                <SelectorContainer>
                    <TotemSelect
                        label="Type"
                        placeholder="Sélectionner store / estore"
                        value={selectedType}
                        options={DELIVERY_TYPES}
                        onChange={setSelectedType}
                        dataTest="create-bundles-type-select"
                    />
                </SelectorContainer>
            ) : null}
            {selectedFlow && selectedType ? (
                <SelectorContainer>
                    <TotemDatePicker
                        label="Date de livraison"
                        selected={deliveryDate}
                        onChange={(date) => {
                            setDeliveryDate(date as Date);
                        }}
                    />
                </SelectorContainer>
            ) : null}
            <SubmitContainer>
                <TotemPrimaryButton
                    onClick={handleSubmit}
                    disabled={!deliveryDate || !selectedFlow || !selectedType || !selectedWarehouseColumnId}
                    data-test="create-bundles-submit"
                    requiredPermissions={[TotadminPermission.BundlesManage]}
                >
                    {loading ? <Loader size="20px" mode={LoaderModeType.Spin} /> : 'Créer les bundles'}
                </TotemPrimaryButton>
            </SubmitContainer>
        </TotemPopup>
    );
}

const SelectorContainer = styled.div`
    margin: 10px 0px;
`;

const SubmitContainer = styled.div`
    display: flex;
    justify-content: center;
    margin-top: 15px;
`;
